import React, { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, Link2, List, ListOrdered, X, Mail } from "lucide-react";
import { getAssessmentTemplates } from "../Services/CompanyServices";
import sidebarImage from "../../../assests/SidebarImage.png";
import SidebarHeaderImage from "../../../assests/SidebarHeaderImage.png";

// Hardcoded locally — no XPRS_Company endpoint exists for listing template types.
// IDs must match what CreateAssessmentTemplate/GetAssessmentTemplates expect.
const TEMPLATE_TYPES = [
  { templateTypeID: 1, templateName: "Invitation" },
  { templateTypeID: 2, templateName: "Reminder" },
  { templateTypeID: 3, templateName: "Completion" },
];

const TEMPLATE_CONTENT = {
  reminder: {
    subject: "Reminder: Please complete the survey",
    body: "",
  },
  invitation: {
    subject: "You're invited to participate in a survey",
    body: "",
  },
  completion: {
    subject: "Thank you for completing the survey",
    body: "",
  },
};

const PARAMETER_OPTIONS = [
  { label: "Responder Name", value: "{{ResponderName}}" },
  { label: "Assessment Name", value: "{{AssessmentName}}" },
  { label: "Assessment Link", value: "{{AssessmentLink}}" },
  { label: "Assessment Deadline", value: "{{AssessmentDeadline}}" },
];

const REQUIRED_SURVEY_LINK_PLACEHOLDER = "{{AssessmentLink}}";

const getLocalContent = (templateName) => {
  const key = (templateName || "").toLowerCase().trim();
  return TEMPLATE_CONTENT[key] || { subject: "", body: "" };
};

const extractSavedTemplate = (data) => {
  if (!data) return null;
  const item = Array.isArray(data) ? data[0] : data;
  if (!item) return null;
  const subject = item.subject ?? item.Subject ?? "";
  const body = item.body ?? item.Body ?? "";
  if (!subject && !body) return null;
  return { subject, body };
};

// assessment expected shape: { assessmentUID, assessmentName, ... }
const SendAssessmentMailModal = ({ isOpen, assessment, onClose, onSend, readOnly = false }) => {
  const [emailType, setEmailType] = useState(String(TEMPLATE_TYPES[0].templateTypeID));
  const [subject, setSubject] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showLinkBox, setShowLinkBox] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);

  const selectedTemplate = TEMPLATE_TYPES.find(
    (t) => String(t.templateTypeID) === emailType
  );
  const selectedTemplateName = (selectedTemplate?.templateName || "").toLowerCase();
  const requiresSurveyLink = !selectedTemplateName.includes("completion");

  const loadTemplateFor = async (typeId, templateName) => {
    const localDefault = getLocalContent(templateName);

    if (!assessment?.assessmentUID || !typeId) {
      setSubject(localDefault.subject);
      if (editorRef.current) editorRef.current.innerHTML = localDefault.body;
      return;
    }

    try {
      const response = await getAssessmentTemplates({
        assessmentUID: assessment.assessmentUID,
        templateTypeID: Number(typeId),
      });

      if (response.data.status?.toLowerCase() === "success") {
        const saved = extractSavedTemplate(response.data.data ?? response.data.Data);
        if (saved) {
          setSubject(saved.subject || localDefault.subject);
          if (editorRef.current) editorRef.current.innerHTML = saved.body || localDefault.body;
          return;
        }
      }
    } catch (error) {
      // best-effort — fall back to local default silently
    }

    setSubject(localDefault.subject);
    if (editorRef.current) editorRef.current.innerHTML = localDefault.body;
  };

  useEffect(() => {
    if (!isOpen) return;
    setShowLinkBox(false);
    setLinkValue("");
    const first = TEMPLATE_TYPES[0];
    setEmailType(String(first.templateTypeID));
    loadTemplateFor(first.templateTypeID, first.templateName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assessment]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleTypeChange = async (id) => {
    setEmailType(id);
    const selected = TEMPLATE_TYPES.find((t) => String(t.templateTypeID) === id);
    await loadTemplateFor(id, selected?.templateName);
    setShowLinkBox(false);
  };

  if (!isOpen) return null;

  const exec = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  };

  const openLinkBox = () => {
    if (showLinkBox) {
      setShowLinkBox(false);
      setLinkValue("");
      return;
    }
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
    setLinkValue("");
    setShowLinkBox(true);
  };

  const insertLink = () => {
    if (!linkValue.trim()) {
      setShowLinkBox(false);
      return;
    }
    editorRef.current?.focus();
    const sel = window.getSelection();
    if (savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
    let url = linkValue.trim();
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;

    if (sel.isCollapsed) {
      document.execCommand(
        "insertHTML",
        false,
        `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>&nbsp;`
      );
    } else {
      document.execCommand("createLink", false, url);
    }
    setShowLinkBox(false);
  };

  const insertParameter = (value) => {
    if (!value) return;
    editorRef.current?.focus();
    document.execCommand("insertText", false, value);
  };

  const handleSubmit = async () => {
    if (!subject.trim()) {
      CustomToast("fail", "Please enter the subject.");
      return;
    }
    const bodyHtml = editorRef.current?.innerHTML || "";
    const bodyText = editorRef.current?.innerText?.trim() || "";
    if (!bodyText) {
      CustomToast("fail", "Please enter the email body.");
      return;
    }

    if (requiresSurveyLink && !bodyHtml.includes(REQUIRED_SURVEY_LINK_PLACEHOLDER)) {
      CustomToast("fail", `Please include ${REQUIRED_SURVEY_LINK_PLACEHOLDER} in the email body.`);
      return;
    }

    const payload = {
      assessmentUID: assessment?.assessmentUID,
      templateTypeID: Number(emailType),
      templateName: selectedTemplate?.templateName || "",
      subject,
      body: bodyHtml,
    };

    try {
      setSubmitting(true);
      await onSend?.(payload); 
      onClose?.();
    } catch (err) {
      CustomToast("fail", err?.response?.data?.message || "Unable to save the template. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-slate-900/40 backdrop-blur-[2px] transition-opacity duration-300 z-[100] ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
      />
      <div
        className={`fixed top-0 right-0 h-full w-full md:w-[720px] bg-white shadow-2xl z-[101] rounded-l-2xl overflow-hidden transform transition-transform duration-300 ease-in-out flex flex-col ${isOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="relative overflow-hidden flex items-center justify-between px-6 py-5 border-b bg-green-50 shrink-0">
          <div
            className="absolute inset-0 bg-no-repeat bg-cover bg-center pointer-events-none"
            style={{ backgroundImage: `url(${SidebarHeaderImage})` }}
          />
          <div className="absolute inset-0 bg-black/35 pointer-events-none" />
          <div className="relative z-10 flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <div className="bg-[#7f35b2]/30 p-2.5 rounded-xl shrink-0">
                <Mail className="text-white" size={22} />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-100">Mail Template</h2>
                <p className="text-sm text-gray-100 mt-0.5">{assessment?.assessmentName || "Manage assessment email content"}</p>
              </div>
            </div>

            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200/60 transition text-gray-100">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="relative flex-1 overflow-hidden">
          <div
            className="absolute inset-0 bg-no-repeat bg-right-bottom opacity-[0.07] pointer-events-none"
            style={{ backgroundImage: `url(${sidebarImage})`, backgroundSize: "360px auto" }}
          />

          <div className="relative h-full overflow-y-auto px-5 py-4 space-y-3.5">
            <div>
              <label className="text-xs text-gray-500">Email Subject</label>
              <select
                value={emailType}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#7f35b2]"
              >
                {TEMPLATE_TYPES.map((t) => (
                  <option key={t.templateTypeID} value={String(t.templateTypeID)}>
                    {t.templateName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs text-gray-500">Subject Line</label>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#7f35b2]"
              />
            </div>

            <div>
              <label className="text-xs text-gray-500">Parameters</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {PARAMETER_OPTIONS.map((parameter) => (
                  <button
                    key={parameter.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertParameter(parameter.value)}
                    className="rounded-full border border-[#c2a8f0] bg-[#7f35b2]/5 px-3 py-1.5 text-xs font-medium text-[#7f35b2] transition hover:border-[#3bb290] hover:bg-[#3bb290]/10 hover:text-[#3bb290]"
                  >
                    {parameter.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500">Body</label>
              <div className="mt-1 border border-gray-200 rounded-lg overflow-visible relative">
                <div className="flex items-center gap-1 border-b border-gray-100 px-2 py-1.5 bg-gray-50 relative">
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} className="p-1 rounded hover:bg-gray-200">
                    <Bold size={14} />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} className="p-1 rounded hover:bg-gray-200">
                    <Italic size={14} />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("underline")} className="p-1 rounded hover:bg-gray-200">
                    <Underline size={14} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openLinkBox}
                    className={`p-1 rounded hover:bg-gray-200 ${showLinkBox ? "bg-gray-200" : ""}`}
                  >
                    <Link2 size={14} />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertOrderedList")} className="p-1 rounded hover:bg-gray-200">
                    <ListOrdered size={14} />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")} className="p-1 rounded hover:bg-gray-200">
                    <List size={14} />
                  </button>

                  {showLinkBox && (
                    <div className="absolute left-0 top-full mt-1 z-20 flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg shadow-lg px-2 py-1.5">
                      <input
                        autoFocus
                        type="text"
                        value={linkValue}
                        onChange={(e) => setLinkValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            insertLink();
                          }
                          if (e.key === "Escape") setShowLinkBox(false);
                        }}
                        placeholder="Paste or type URL"
                        className="w-48 border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#7f35b2]"
                      />
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={insertLink}
                        className="px-2 py-1 rounded-md text-xs text-white bg-[#7f35b2] hover:bg-[#6a2c96]"
                      >
                        Insert
                      </button>
                    </div>
                  )}
                </div>

                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  className="rte-body min-h-[100px] max-h-[160px] overflow-y-auto px-3 py-2 text-sm text-gray-700 focus:outline-none"
                />
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                Placeholders <code>{`{{ResponderName}}`}</code>, <code>{`{{AssessmentName}}`}</code>, <code>{`{{AssessmentLink}}`}</code> get replaced automatically per responder. {!requiresSurveyLink ? "Assessment link is optional for completion templates." : <><code>{`{{AssessmentLink}}`}</code> is required.</>}
              </p>
            </div>
          </div>
        </div>

        {!readOnly && (
          <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-100 shrink-0">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-5 py-2 rounded-lg text-sm text-white bg-[#7f35b2] hover:bg-[#34a27f] disabled:opacity-60"
            >
              {submitting ? "Saving..." : "Save Template"}
            </button>
          </div>
        )}
      </div>

      <style>{`
        .rte-body ul { list-style: disc; padding-left: 1.25rem; margin: 0.25rem 0; }
        .rte-body ol { list-style: decimal; padding-left: 1.25rem; margin: 0.25rem 0; }
        .rte-body li { margin: 0.15rem 0; }
        .rte-body a { color: #7f35b2; text-decoration: underline; }
        .rte-body b, .rte-body strong { font-weight: 700; }
        .rte-body i, .rte-body em { font-style: italic; }
        .rte-body u { text-decoration: underline; }
      `}</style>
    </>
  );
};

export default SendAssessmentMailModal;
